<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { cloneDeep } from 'lodash-es';

import { createFinancials } from '@/helpers/builder';
import { EmploymentType } from '@/helpers/const';
import { useTemplateRefsList } from '@vueuse/core';
import { getFullName } from '@/helpers/format';
import { useCustomerQuoteProcess } from '@/helpers/useCustomerQuoteProcess';
import { CheckIcon, XMarkIcon } from '@heroicons/vue/20/solid';

import IncomeForm from './modules/Income/IncomeForm.vue';
// import SimpleComprehensive from './modules/SimpleComprehensive.vue';
import SimpleSalaryForm from './modules/Income/SimpleSalaryForm.vue';

import type { ApplicantFinancials, ApplicantId } from '@/types';
import BxCard from '@/components/common/BxCard.vue';

const { loanApp, loanApplicants, sectionStates, saveAndGoNext, goBack } = useCustomerQuoteProcess();

const error = ref('');

const incomeType = computed(() => sectionStates.value.income);
const validApplicants = computed(() => loanApp.value.applicants.map((a) => a.id));

const initFinancials = validApplicants.value.reduce((obj, item) => {
  obj[item] = createFinancials();
  return obj;
}, {} as ApplicantFinancials);

const financials = ref({ ...initFinancials, ...cloneDeep(loanApp.value.financials) });
const simpleSalaryFormRefs = useTemplateRefsList<InstanceType<typeof SimpleSalaryForm>>();

const activeTab = ref<ApplicantId | undefined>(
  validApplicants.value.length === 1 ? validApplicants.value[0] : undefined,
);
const incomeFormsRefs = useTemplateRefsList<InstanceType<typeof IncomeForm>>();

const validateIncome = async () => {
  let isValid = true;

  if (incomeType.value === 'simple') {
    const refCount = simpleSalaryFormRefs.value.length;
    if (refCount > 0) {
      for (let i = 0; i < refCount; i++) {
        const formIsValid = (await simpleSalaryFormRefs.value[i].update()) ?? false;
        if (!formIsValid) {
          isValid = false;
        }
      }
      if (!isValid) {
        window.scrollTo(0, 0);
      }
    } else {
      isValid = false;
    }
  } else {
    const refCount = incomeFormsRefs.value.length;
    for (let i = 0; i < refCount; i++) {
      isValid = (await incomeFormsRefs.value[i].update()) ?? false;
      if (!isValid) {
        window.scrollTo(0, 0);
        activeTab.value = validApplicants.value[i];
        break;
      }
    }
  }

  return isValid;
};

const saveAndContinue = async () => {
  if (await validateIncome()) {
    if (incomeType.value === 'comprehensive' && !financials.value['applicant-0'].income?.employment.length) {
      error.value = 'Income source is required.';
      return;
    }
    error.value = '';

    return saveAndGoNext({
      financials: financials.value,
    });
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const cardTitle = (employment: any) => {
  let description = '';
  if (employment.type === EmploymentType.SELF_EMPLOYED) {
    description = employment.tradingName;
  } else if (
    employment.type === EmploymentType.FULL_TIME ||
    employment.type === EmploymentType.PART_TIME ||
    employment.type === EmploymentType.CASUAL
  ) {
    description = employment.employerName;
  }
  return `${employment.type.charAt(0).toUpperCase() + employment.type.slice(1).replace('_', ' ')}: ${description}`;
};

const cardDescription = (applicantId: ApplicantId) => {
  return financials.value[applicantId].income?.employment?.map((item) => {
    return cardTitle(item);
  });
};

watch(
  incomeType,
  (newType, oldType) => {
    const isRelevantChange =
      (newType === 'simple' && oldType === 'comprehensive') || (newType === 'comprehensive' && oldType === 'simple');

    if (!isRelevantChange) return;

    for (const applicantId of validApplicants.value) {
      financials.value[applicantId].income = null;
    }
  },
  { immediate: true },
);
</script>

<template>
  <div>
    <div class="mb-8 pb-2">
      <h2 class="text-2xl font-medium">
        Enter
        <BxHighlight>income</BxHighlight>
        for each applicant
      </h2>
    </div>
    <!-- <div class="my-4">
      <SimpleComprehensive :section-key="'income'" title="Incoming" />
      <p class="pt-4">
        Use the Quick vs Comprehensive toggle to choose whether you want to enter a total income amount or a full
        breakdown of income sources.
      </p>
    </div> -->
    <div class="my-6 grid gap-4">
      <div v-for="applicantId in validApplicants" :key="applicantId">
        <div v-if="incomeType === 'simple'">
          <BxCard>
            <h3 class="mb-8 flex items-center text-lg font-medium leading-6 text-gray-900">
              <span class="mr-3 hidden md:block"><BxIcon name="customerIncome" /></span>
              <span>{{ getFullName(loanApplicants[applicantId]) }}</span>
            </h3>
            <SimpleSalaryForm
              :ref="simpleSalaryFormRefs.set"
              v-model="financials[applicantId].income"
              :applicant-id="applicantId"
            />
          </BxCard>
        </div>
        <div v-else>
          <BxCard :class="applicantId !== activeTab && 'hidden'">
            <h3 class="mb-8 flex items-center text-lg font-medium leading-6 text-gray-900">
              <span class="mr-3 hidden md:block"><BxIcon name="customerIncome" /></span>
              <span>{{ getFullName(loanApplicants[applicantId]) }}</span>
            </h3>
            <IncomeForm
              :ref="incomeFormsRefs.set"
              v-model="financials[applicantId].income"
              :applicant-id="applicantId"
              :animate="applicantId === activeTab"
              :show-buttons="validApplicants.length > 1"
              @save="activeTab = undefined"
              @cancel="activeTab = undefined"
            />
          </BxCard>
          <BxCard :class="`${applicantId === activeTab && 'hidden'} employment-card`">
            <div class="flex items-center">
              <div class="card-details">
                <div class="mb-2 font-medium">{{ getFullName(loanApplicants[applicantId]) }}</div>
                <div class="flex items-center gap-2">
                  <div class="h-5 w-5">
                    <CheckIcon v-if="financials[applicantId].income?.employment.length" class="text-green-600" />
                    <XMarkIcon v-else class="text-rose-600" />
                  </div>
                  Employment details
                </div>
                <div class="flex items-center gap-2">
                  <div class="h-5 w-5">
                    <CheckIcon
                      v-if="financials[applicantId].income?.expectIncomeChange !== undefined"
                      class="text-green-600"
                    />
                    <XMarkIcon v-else class="text-rose-600" />
                  </div>
                  Additional income
                </div>
              </div>
              <div class="employment-descriptions ml-6">
                <div>
                  <div v-for="description in cardDescription(applicantId)" :key="description">
                    <span class="text-gray-400">{{ description }}</span>
                  </div>
                </div>
              </div>
              <div class="card-button">
                <BxButton
                  :id="applicantId + '-add-income'"
                  variant="secondary"
                  :disabled="activeTab && activeTab !== applicantId"
                  @click="activeTab = applicantId"
                >
                  {{ financials[applicantId].income?.employment.length ? 'Edit' : 'Add' }} income
                </BxButton>
              </div>
            </div>
          </BxCard>
          <BxCard :class="`${applicantId === activeTab && 'hidden'} employment-card-mobile`">
            <div class="items-left flex flex-col">
              <div class="card-details">
                <div class="mb-2 font-medium">{{ getFullName(loanApplicants[applicantId]) }}</div>
                <div class="items-left flex gap-2">
                  <div class="h-5 w-5">
                    <CheckIcon v-if="financials[applicantId].income?.employment.length" class="text-green-600" />
                    <XMarkIcon v-else class="text-rose-600" />
                  </div>
                  Employment details
                </div>
                <div class="items-left flex gap-2">
                  <div class="h-5 w-5">
                    <CheckIcon
                      v-if="financials[applicantId].income?.expectIncomeChange !== undefined"
                      class="text-green-600"
                    />
                    <XMarkIcon v-else class="text-rose-600" />
                  </div>
                  Additional income
                </div>
              </div>
              <div class="employment-descriptions ml-6">
                <div>
                  <div v-for="description in cardDescription(applicantId)" :key="description">
                    <span class="text-gray-400">{{ description }}</span>
                  </div>
                </div>
              </div>
            </div>
            <div class="card-button">
              <BxButton
                variant="secondary"
                :disabled="activeTab && activeTab !== applicantId"
                @click="activeTab = applicantId"
              >
                {{ financials[applicantId].income?.employment.length ? 'Edit' : 'Add' }} income
              </BxButton>
            </div>
          </BxCard>
          <span v-if="error" class="mt-6 block text-sm text-rose-500">
            {{ error }}
          </span>
        </div>
      </div>
    </div>
    <div class="mt-6">
      <BxButton id="income-continue" variant="primary" @click="saveAndContinue()">Save and continue</BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.section-panel {
  --border-radius: 0.5rem;
  --border-color: #ccc;

  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);

  background-color: #fff;
}

.employment-descriptions {
  @media (max-width: 767px) {
    margin-top: 20px;
    margin-bottom: 20px;
  }
}
.employment-card {
  @media (max-width: 767px) {
    display: none;
  }
}

.employment-card-mobile {
  @media (min-width: 767px) {
    display: none;
  }
}

.card-button {
  flex-grow: 1;
  @media (min-width: 767px) {
    text-align: right;
  }
  @media (max-width: 767px) {
    text-align: center;
  }
  flex-basis: 200px;
}
</style>
